// Fuzzy-name matcher for picking the best candidate out of a group that
// share another attribute (e.g. multiple ADO builds / releases under the
// same Bitbucket repo). Uses Jaccard similarity:
//   score = |hint ∩ candidate| / |hint ∪ candidate|
//
// Jaccard naturally penalises candidates with extra non-matching tokens
// — important for pipeline names where prefixes like "BDirect DataFactory"
// or "AX OPRI Attributes" add tokens that aren't in the step text. A
// token-count-only score lets "AX OPRI Attributes Dataloader Data Factory"
// outrank "Dataloader Data Factory" for a "Dataloader Data Factory" hint
// (both share `dataloader`, but the longer name has irrelevant extras).
//
// useCatalog.ts has a different matcher (tiered: exact / exactLen / loose
// substring) tuned for catalog component disambiguation; we keep this one
// separate because pipeline names have a different shape — they typically
// stand alone rather than sharing a long common prefix.

const tokeniseRaw = (raw: string): string[] => {
  const plain = raw
    .replace(/<[^>]*>/g, ' ')
    // Split "ABCWord" → "ABC Word" (acronym followed by Word).
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // Split "camelCase" → "camel Case".
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return Array.from(
    new Set(plain.split(/[^a-z0-9]+/).filter(t => t.length >= 2)),
  );
};

// Tokens shared by every candidate carry no discriminative signal — drop
// them so the hint's actually-distinguishing tokens drive the match.
const computeCommonTokens = (corpuses: string[]): Set<string> => {
  if (corpuses.length < 2) return new Set();
  const sets = corpuses.map(c => new Set(tokeniseRaw(c)));
  const common = new Set<string>();
  for (const t of sets[0]) {
    if (sets.every(s => s.has(t))) common.add(t);
  }
  return common;
};

export const pickBestByFuzzyMatch = <T>(
  candidates: readonly T[],
  corpusOf: (c: T) => string,
  hint: string,
): T | undefined => {
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];

  const corpuses = candidates.map(corpusOf);
  const common = computeCommonTokens(corpuses);
  const hintTokens = new Set(
    tokeniseRaw(hint).filter(t => !common.has(t)),
  );
  if (hintTokens.size === 0) return candidates[0];

  // Tokenise candidates once and compute document frequency per hint
  // token across the candidate pool. A token that appears in only one
  // candidate (e.g. "movement") is a much stronger signal than one
  // that appears in nine (e.g. "factory") — IDF lets the tiebreaker
  // capture that.
  const candTokenSets = corpuses.map(
    c => new Set(tokeniseRaw(c).filter(t => !common.has(t))),
  );
  const df = new Map<string, number>();
  for (const t of hintTokens) {
    let count = 0;
    for (const set of candTokenSets) {
      if (set.has(t)) count++;
    }
    df.set(t, count);
  }
  // Smoothed IDF (1 / (df+1)) — avoids div-by-zero for tokens absent
  // from all candidates and bounds the weight at 1.0.
  const idfFor = (t: string) => 1 / ((df.get(t) ?? 0) + 1);

  let best = candidates[0];
  let bestScore: [number, number, number] = [-1, -1, -1];
  for (let i = 0; i < candidates.length; i++) {
    const candTokens = candTokenSets[i];
    let intersection = 0;
    let weightedIntersection = 0;
    for (const t of hintTokens) {
      if (candTokens.has(t)) {
        intersection++;
        weightedIntersection += idfFor(t);
      }
    }
    const unionSize = hintTokens.size + candTokens.size - intersection;
    const jaccard = unionSize === 0 ? 0 : intersection / unionSize;
    // Tiered scoring:
    //   1. Jaccard — penalises candidates with extra non-matching tokens
    //      (so "Dataloader Data Factory" beats "AX OPRI Attributes
    //      Dataloader Data Factory" for a "Dataloader" hint, both share
    //      1 token but the longer name has more irrelevant extras).
    //   2. IDF-weighted intersection — breaks Jaccard ties by preferring
    //      candidates that match rare/distinctive hint tokens (so an
    //      "OPRI Data Movement …" candidate beats an "OPRI Template Sync
    //      …" candidate when both share `opri` + one common token, and
    //      one of the other tokens is much rarer in the pool).
    //   3. Raw intersection size — final tiebreak.
    const score: [number, number, number] = [
      jaccard,
      weightedIntersection,
      intersection,
    ];
    // Strict > keeps first-seen on full ties (deterministic across renders).
    if (
      score[0] > bestScore[0] ||
      (score[0] === bestScore[0] && score[1] > bestScore[1]) ||
      (score[0] === bestScore[0] &&
        score[1] === bestScore[1] &&
        score[2] > bestScore[2])
    ) {
      best = candidates[i];
      bestScore = score;
    }
  }
  return best;
};
